/**
 * Block 12 PR-Hotfix-2B Phase B5 Step 12 — FacadeRadiusBlock primitive (Bug 14).
 *
 * Reusable compound для row.id === 'facade-radius':
 *   - ToggleableRow (toggle + qty + price + cost) — generic primitive
 *   - жёлтая кнопка «Найти и узнать цену» → /partners (PR-calc-buttons-unify,
 *     ADR-0099 — Container делает forcePersist + navigate) — when enabled
 *   - RadioCard с 5 типами radius фасада (методика §2.1 Б.2):
 *       пленка модерн / пленка рефленка / эмаль модерн / эмаль рефленка / шпон
 *
 * Раньше (Phase B4 Step 3) facade-radius rendered inline в SectionAdditional —
 * extraction позволяет использовать тот же UI block в SectionFilling для Cabinet
 * (Phase B5 Step 12 — Bug 14 structure: Cabinet facade-radius переносится из
 * Section В в Section Г Наполнение per прототип 2.12.3:411-440).
 *
 * Состояние хранится в `state.additionalToggles/Quantities/Prices/Selections`
 * keyed by `'facade-radius'` (same map для всех anketas) — Cabinet'у не нужны
 * отдельные state shapes для facade-radius в Section Г vs Section В.
 *
 * Reference: ADR-0020 §Q1; методика §2.1 Б.2; Hotfix-2B TZ §6 Step 12; прототип
 * 2.12.3:411-440 (Cabinet Section Г) + 2.12.2:456 (Kitchen Section В).
 */

import { RadioCard } from '@/components/forms/RadioCard';
import { ADDITIONAL_META } from '../additionalMeta';
import { ToggleableRow } from '../sections/ToggleableRow';

export const FACADE_RADIUS_ROW_ID = 'facade-radius';

/**
 * 5 типов radius фасада per методика §2.1 Б.2. Scope ровно один consumer
 * (этот sub-picker); inline constant. Если в будущем sub-picker появится для
 * других additional ids или Block — extract'нуть.
 */
const RADIUS_FACADE_KINDS = [
  'пленка модерн',
  'пленка рефленка',
  'эмаль модерн',
  'эмаль рефленка',
  'шпон',
] as const;

export interface FacadeRadiusBlockProps {
  enabled: boolean;
  qty: number;
  price: number;
  /** Текущий выбранный тип фасада. Undefined → пользователь не выбрал. */
  selection: string | undefined;
  onToggle: (on: boolean) => void;
  onQty: (qty: number) => void;
  onPrice: (price: number) => void;
  onSelection: (value: string) => void;
  /** Container-provided lookup для validation error display. */
  errorForPath?: (path: string) => string | undefined;
  /** PR-calc-buttons-unify (ADR-0099) — жёлтая кнопка «Найти и узнать цену» → /partners. */
  onDeeplink?: () => void | Promise<void>;
  /**
   * PR-H1 Round 3 — pass-through brand props в underlying ToggleableRow
   * (которая pass-through в ResultRow). Render gated на `onBrandChange`.
   */
  brand?: string;
  onBrandChange?: (brand: string) => void;
}

export function FacadeRadiusBlock({
  enabled,
  qty,
  price,
  selection,
  onToggle,
  onQty,
  onPrice,
  onSelection,
  errorForPath,
  onDeeplink,
  brand,
  onBrandChange,
}: FacadeRadiusBlockProps) {
  const meta = ADDITIONAL_META[FACADE_RADIUS_ROW_ID]!;
  return (
    <div className="flex flex-col gap-2" data-testid="facade-radius-block">
      <ToggleableRow
        id={FACADE_RADIUS_ROW_ID}
        label={meta.label}
        unit={meta.unit}
        enabled={enabled}
        qty={qty}
        price={price}
        onToggle={onToggle}
        onQty={onQty}
        onPrice={onPrice}
        deeplink="partner"
        onDeeplink={onDeeplink}
        brand={brand}
        onBrandChange={onBrandChange}
      />
      {enabled ? (
        <RadioCard
          name={`radius-facade-kind-${FACADE_RADIUS_ROW_ID}`}
          label="Тип радиусного фасада"
          options={RADIUS_FACADE_KINDS.map((kind) => ({ value: kind, label: kind }))}
          value={selection ?? null}
          onChange={onSelection}
          required
          error={errorForPath?.(`additionalSelections.${FACADE_RADIUS_ROW_ID}`)}
        />
      ) : null}
    </div>
  );
}
